import { ErrorMessage, Flex, Grid, NumberField } from "@simplybusiness/mobius";
import { useSnowplowContext } from "@simplybusiness/services";
import type { QuestionComponentProps } from "../..";
import {
  useCallback,
  useRef,
  useState,
  Fragment,
  useMemo,
  useId,
  type JSX,
} from "react";
import {
  clampForLabel,
  computeTotal,
  buildInitialGroupPercentages,
  type NormalizedLabel,
} from "./percentageQuestionUtils";
import { useAnswers } from "../../../../../../../features/questionnaire";
import { useQuestionnaire } from "../../../../../../../features/questionnaire";
import { snakeCase } from "../../../../../../../utils";

export const PercentageGroupQuestion = ({
  questionFieldProps,
  config,
  sectionTitle,
}: QuestionComponentProps): JSX.Element => {
  const answers = useAnswers();
  const { groups } = useQuestionnaire();
  const { snowplow } = useSnowplowContext();
  const {
    value,
    validation,
    onChange,
    // we deliberately extract onBlur so it isn't passed to each field
    onBlur,
    questionLabelProps,
    helpInfo,
    errorMessage,
    name,
    id,
    ...otherProps
  } = questionFieldProps;

  const { labels, step: configStep } = config || {};

  // Parse step from config with fallback to 1
  const step = useMemo(() => {
    const parsedStep = Number(configStep);
    return Number.isFinite(parsedStep) && parsedStep > 0 ? parsedStep : 1;
  }, [configStep]);

  // Get the group information to map labels to original question names
  const group = groups[name];

  // Normalize labels once - use original question names as keys for data storage
  const normalizedLabels = useMemo((): NormalizedLabel[] => {
    if (!Array.isArray(labels) || !group?.names || group.kind !== "percent") {
      return [];
    }

    const normalized = (labels as string[]).map((label, index) => ({
      display: label,
      key: group.names[index],
    }));

    // Safety check: ensure we have at least 2 labels (regular questions + total)
    if (normalized.length < 2) {
      console.warn(
        `PercentageGroupQuestion: Expected at least 2 labels, got ${normalized.length}`,
      );
    }

    return normalized;
  }, [labels, group]);

  const regularQuestionNormalizedLabels = normalizedLabels.slice(0, -1);
  const totalQuestionNormalizedLabel =
    normalizedLabels[normalizedLabels.length - 1];

  // Build initial state from original question names in answers (excluding total)
  const [percentages, setPercentages] = useState<Record<string, number>>(() =>
    buildInitialGroupPercentages(regularQuestionNormalizedLabels, answers),
  );

  const total = computeTotal(percentages);

  const errorMessageId = useId();

  const containerRef = useRef<HTMLDivElement | null>(null);

  // Create memoized change handlers for each field
  const handlers = useMemo(() => {
    return regularQuestionNormalizedLabels.reduce(
      (acc, { key }) => {
        acc[key] = (e: React.ChangeEvent<HTMLInputElement>) => {
          const newValue = Number(e.target.value);
          setPercentages(prev => ({
            ...prev,
            [key]: newValue,
          }));

          // Trigger snowplow event for the individual field change
          snowplow?.trigger("questionAnswered", {
            section: sectionTitle ?? "",
            question: snakeCase(key),
            answer: `${newValue}%`,
          });
        };
        return acc;
      },
      {} as Record<string, (e: React.ChangeEvent<HTMLInputElement>) => void>,
    );
  }, [regularQuestionNormalizedLabels, snowplow, sectionTitle]);

  // Fire onBlur only when focus leaves the entire component
  const handleContainerBlur = useCallback(
    (e: React.FocusEvent) => {
      const nextFocus = e.relatedTarget as Node | null;
      if (nextFocus && containerRef.current?.contains(nextFocus)) {
        // Focus is still within the component; ignore
        return;
      }
      if (onBlur) {
        // Map percentages to original question names with % suffix
        const answersByOriginalName: Record<string, string> = {};

        // Keys are already the original question names, so just add % suffix
        regularQuestionNormalizedLabels.forEach(({ key }) => {
          answersByOriginalName[key] = `${percentages[key] || 0}%`;
        });

        answersByOriginalName[totalQuestionNormalizedLabel.key] = `${total}%`;

        // Send answers using original question names
        onBlur({
          target: {
            name: questionFieldProps.name,
            value: answersByOriginalName,
          },
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onBlur, percentages, questionFieldProps.name, normalizedLabels],
  );

  return (
    <div onBlur={handleContainerBlur} ref={containerRef} tabIndex={-1}>
      <Flex flexDirection="column" gap="sm">
        <Grid columns={2} alignItems="center" rowGap="sm">
          {regularQuestionNormalizedLabels.map(({ display, key }) => (
            <Fragment key={`${id}-${display}`}>
              <label id={`${id}-${display}-label`} htmlFor={`${id}-${display}`}>
                {display}
              </label>
              <NumberField
                {...otherProps}
                step={step}
                id={`${id}-${display}`}
                aria-labelledby={`${id}-${display}-label`}
                aria-errormessage={errorMessage ? errorMessageId : undefined}
                aria-invalid={!!errorMessage}
                onChange={handlers[key]}
                value={String(percentages[key] || 0)}
                // Do NOT pass onBlur so validation waits for container blur
                minValue={0}
                maxValue={clampForLabel(percentages, key, 100)}
                suffixInside={<span>%</span>}
              />
            </Fragment>
          ))}
          <label>{totalQuestionNormalizedLabel.display}</label>
          <span className="mobius-percentage-question__total">{total}%</span>
        </Grid>
        {!!errorMessage && (
          <ErrorMessage id={errorMessageId} errorMessage={errorMessage} />
        )}
      </Flex>
    </div>
  );
};
